import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import {
  Anchor,
  Lock,
  Loader2,
  LogOut,
  Megaphone,
  Server,
  TrendingUp,
  Users,
  Percent,
  Wallet,
  RefreshCw,
  Inbox,
} from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "helm.token";

const currency = (n) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(n || 0);

const BAR_PALETTE = [
  "#0C2340",
  "#16355E",
  "#1E3A5F",
  "#2E4A68",
  "#3A5C82",
  "#4A6E96",
  "#5F82A8",
  "#7A9AC0",
];

/* ------------------------------ Password gate ----------------------------- */

function PasswordGate({ onAuthenticated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API}/api/helm/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!resp.ok) {
        setError("Incorrect email or password.");
        setLoading(false);
        return;
      }
      const data = await resp.json();
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onAuthenticated(data.token);
    } catch (err) {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-[#E9EEF5] px-6" data-testid="helm-gate">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{
        backgroundImage:
          "radial-gradient(circle at 20% 15%, rgba(30,58,95,0.10), transparent 55%), radial-gradient(circle at 80% 85%, rgba(12,35,64,0.08), transparent 55%)",
      }} />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-sm card-light rounded-sm p-8"
      >
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340] text-white">
            <Lock size={18} strokeWidth={1.6} />
          </span>
          <div>
            <p className="overline overline-dark">/ Restricted</p>
            <h1 className="font-display text-2xl text-[#0C2340]">The Helm</h1>
          </div>
        </div>
        <p className="mb-6 font-body text-sm leading-relaxed text-[#1E3A5F]">
          Executive dashboard access. Enter your credentials to continue.
        </p>
        <label className="overline overline-dark mb-2 block">Email</label>
        <input
          type="email"
          autoFocus
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4 w-full rounded-sm border border-[#0C2340]/15 bg-white px-4 py-3 font-body text-[#0C2340] outline-none transition-colors focus:border-[#0C2340]/60"
          data-testid="helm-email-input"
        />
        <label className="overline overline-dark mb-2 block">Password</label>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-sm border border-[#0C2340]/15 bg-white px-4 py-3 font-body text-[#0C2340] outline-none transition-colors focus:border-[#0C2340]/60"
          data-testid="helm-password-input"
        />
        {error && (
          <p className="mt-3 font-mono-2 text-[0.7rem] uppercase tracking-[0.16em] text-[#8b2b2b]" data-testid="helm-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !email || !password}
          className="btn-ink mt-6 flex w-full items-center justify-center gap-2 rounded-sm px-6 py-3 font-mono-2 text-[0.72rem] uppercase tracking-[0.2em] disabled:opacity-60"
          data-testid="helm-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Anchor size={14} />}
          Enter
        </button>
      </motion.form>
    </div>
  );
}

/* -------------------------------- Dashboard ------------------------------- */

const StatCard = ({ icon: Icon, label, value, hint, badge, badgeTone = "muted", testid }) => {
  const toneClasses =
    badgeTone === "error"
      ? "border-[#8b2b2b]/30 text-[#8b2b2b]"
      : badgeTone === "live"
      ? "border-[#0C2340]/25 bg-[#0C2340]/[0.04] text-[#0C2340]"
      : "border-[#0C2340]/15 text-[#2E4A68]";
  return (
    <motion.div variants={fadeUp} className="card-light rounded-sm p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
          <Icon size={18} strokeWidth={1.5} />
        </span>
        {badge && (
          <span className={`font-mono-2 rounded-full border px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] ${toneClasses}`}>
            {badge}
          </span>
        )}
      </div>
      <p className="overline overline-dark mt-6">{label}</p>
      <p className="mt-2 font-display text-4xl text-[#0C2340]">{value}</p>
      {hint && <p className="mt-2 font-body text-xs text-[#2E4A68]">{hint}</p>}
    </motion.div>
  );
};

function FinancialsChart({ projects }) {
  const data = useMemo(
    () => projects.map((p) => ({ name: p.project, credit: p.credit_cad })),
    [projects]
  );
  return (
    <div className="h-[360px] w-full" data-testid="helm-financials-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 40 }}>
          <CartesianGrid stroke="rgba(12,35,64,0.08)" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fill: "#1E3A5F", fontSize: 11, fontFamily: "Outfit" }}
            interval={0}
            angle={-20}
            textAnchor="end"
            height={60}
            stroke="rgba(12,35,64,0.2)"
          />
          <YAxis
            tick={{ fill: "#1E3A5F", fontSize: 11, fontFamily: "Outfit" }}
            stroke="rgba(12,35,64,0.2)"
            tickFormatter={(v) => `$${v}`}
          />
          <Tooltip
            cursor={{ fill: "rgba(12,35,64,0.05)" }}
            contentStyle={{
              background: "#ffffff",
              border: "1px solid rgba(12,35,64,0.15)",
              borderRadius: 2,
              fontFamily: "Outfit",
              color: "#0C2340",
            }}
            formatter={(v) => [currency(v), "Credit Investment"]}
          />
          <Bar dataKey="credit" radius={[2, 2, 0, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={BAR_PALETTE[i % BAR_PALETTE.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Dashboard({ token, onSignOut }) {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [finResp, subsResp] = await Promise.all([
        fetch(`${API}/api/helm/financials`, { headers: { "X-Helm-Token": token } }),
        fetch(`${API}/api/helm/subscriptions`, { headers: { "X-Helm-Token": token } }),
      ]);
      if (finResp.status === 401 || subsResp.status === 401) {
        onSignOut();
        return;
      }
      if (!finResp.ok) throw new Error("Failed to load financials");
      setData(await finResp.json());
      setSubs(subsResp.ok ? await subsResp.json() : { source: "error", message: "Unable to connect — check API key" });
    } catch (err) {
      setError("Could not load financials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const projects = data?.projects || [];
  const totals = useMemo(() => {
    return projects.reduce(
      (acc, p) => {
        acc.credit += p.credit_cad || 0;
        acc.marketing += p.marketing_cad || 0;
        acc.hosting += p.hosting_cad || 0;
        return acc;
      },
      { credit: 0, marketing: 0, hosting: 0 }
    );
  }, [projects]);

  return (
    <div className="grain relative min-h-screen bg-[#E9EEF5] pb-24" data-testid="helm-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 glass-nav-light">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Tide Water Studios" className="h-9 w-9 object-cover logo-medallion" />
            <div>
              <p className="overline overline-dark text-[0.6rem]">/ Executive Dashboard</p>
              <h1 className="font-display text-xl text-[#0C2340]">The Helm</h1>
              <p className="font-mono-2 mt-0.5 text-[0.58rem] uppercase tracking-[0.18em] text-[#2E4A68]" data-testid="helm-corp-meta">
                BN 846581 &middot; Incorporated 20 Jul 2026
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/the-helm/inbox"
              className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-2 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
              data-testid="helm-inbox-link"
            >
              <Inbox size={12} />
              Inbox
            </Link>
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-2 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
              data-testid="helm-refresh"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={onSignOut}
              className="flex items-center gap-2 rounded-sm border border-[#0C2340]/15 bg-white px-3 py-2 font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#0C2340] transition-colors hover:border-[#0C2340]/40"
              data-testid="helm-signout"
            >
              <LogOut size={12} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 pt-12 md:px-10">
        {/* Intro */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14"
        >
          <motion.p variants={fadeUp} className="overline overline-dark mb-5">/ Overview</motion.p>
          <motion.h2 variants={fadeUp} className="font-display text-5xl tracking-tight text-[#0C2340] md:text-6xl">
            Steering the <span>Studio</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 max-w-2xl font-body text-base leading-relaxed text-[#1E3A5F]">
            A private view of investment, marketing spend, and product traction across the fleet.
          </motion.p>
        </motion.div>

        {/* Financials */}
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-20"
          data-testid="helm-section-financials"
        >
          <div className="mb-8 flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              <motion.p variants={fadeUp} className="overline overline-dark mb-3">/ Financials</motion.p>
              <motion.h3 variants={fadeUp} className="font-display text-3xl text-[#0C2340] md:text-4xl">
                Credit Investment by Project
              </motion.h3>
            </div>
            <motion.div variants={fadeUp} className="flex items-center gap-3">
              <span className="font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">
                Source: {data?.source === "sheets" ? "Google Sheets (live)" : "Sample fallback"}
              </span>
            </motion.div>
          </div>

          <motion.div variants={fadeUp} className="card-light rounded-sm p-6">
            {loading ? (
              <div className="flex h-[360px] items-center justify-center text-[#1E3A5F]">
                <Loader2 className="animate-spin" size={22} />
              </div>
            ) : error ? (
              <div className="flex h-[360px] items-center justify-center font-mono-2 text-[0.7rem] uppercase tracking-[0.2em] text-[#8b2b2b]">
                {error}
              </div>
            ) : (
              <FinancialsChart projects={projects} />
            )}

            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#0C2340]/10 pt-6 md:grid-cols-4">
              <div>
                <p className="overline overline-dark">Total Credit</p>
                <p className="mt-1 font-display text-2xl text-[#0C2340]" data-testid="helm-total-credit">
                  {currency(totals.credit)}
                </p>
              </div>
              <div>
                <p className="overline overline-dark">Marketing</p>
                <p className="mt-1 font-display text-2xl text-[#0C2340]">{currency(totals.marketing)}</p>
              </div>
              <div>
                <p className="overline overline-dark">Hosting / Domain</p>
                <p className="mt-1 font-display text-2xl text-[#0C2340]">{currency(totals.hosting)}</p>
              </div>
              <div>
                <p className="overline overline-dark">Projects</p>
                <p className="mt-1 font-display text-2xl text-[#0C2340]">{projects.length}</p>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* Marketing */}
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-20"
          data-testid="helm-section-marketing"
        >
          <div className="mb-8">
            <motion.p variants={fadeUp} className="overline overline-dark mb-3">/ Marketing</motion.p>
            <motion.h3 variants={fadeUp} className="font-display text-3xl text-[#0C2340] md:text-4xl">
              Meta Ads &amp; Infrastructure
            </motion.h3>
            <motion.p variants={fadeUp} className="mt-3 max-w-2xl font-body text-sm text-[#1E3A5F]">
              Ad spend vs. hosting and domain costs by project.
            </motion.p>
          </div>

          <motion.div variants={fadeUp} className="card-light overflow-hidden rounded-sm">
            <table className="w-full" data-testid="helm-marketing-table">
              <thead>
                <tr className="border-b border-[#0C2340]/10 bg-[#0C2340]/[0.03]">
                  <th className="px-6 py-4 text-left font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Project</th>
                  <th className="px-6 py-4 text-right font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">
                    <span className="inline-flex items-center gap-2"><Megaphone size={12} /> Meta Ads (CAD)</span>
                  </th>
                  <th className="px-6 py-4 text-right font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">
                    <span className="inline-flex items-center gap-2"><Server size={12} /> Hosting / Domain (CAD)</span>
                  </th>
                  <th className="px-6 py-4 text-right font-mono-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#2E4A68]">Total</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => {
                  const total = (p.marketing_cad || 0) + (p.hosting_cad || 0);
                  return (
                    <tr
                      key={p.project}
                      className={`border-b border-[#0C2340]/5 transition-colors hover:bg-[#0C2340]/[0.02] ${
                        i === projects.length - 1 ? "border-b-0" : ""
                      }`}
                      data-testid={`helm-marketing-row-${i}`}
                    >
                      <td className="px-6 py-4 font-body text-[#0C2340]">{p.project}</td>
                      <td className="px-6 py-4 text-right font-body text-[#1E3A5F]">{currency(p.marketing_cad)}</td>
                      <td className="px-6 py-4 text-right font-body text-[#1E3A5F]">{currency(p.hosting_cad)}</td>
                      <td className="px-6 py-4 text-right font-display text-lg text-[#0C2340]">{currency(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        </motion.section>

        {/* App Performance */}
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-20"
          data-testid="helm-section-performance"
        >
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <motion.p variants={fadeUp} className="overline overline-dark mb-3">/ App Performance</motion.p>
              <motion.h3 variants={fadeUp} className="font-display text-3xl text-[#0C2340] md:text-4xl">
                Traction &amp; Conversion
              </motion.h3>
            </div>
            <motion.span
              variants={fadeUp}
              className={`font-mono-2 rounded-full border px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] ${
                subs?.source === "revenuecat"
                  ? "border-[#0C2340]/25 bg-[#0C2340]/[0.04] text-[#0C2340]"
                  : "border-[#8b2b2b]/30 text-[#8b2b2b]"
              }`}
              data-testid="helm-rc-status"
            >
              {subs?.source === "revenuecat" ? "RevenueCat — Live" : "RevenueCat — Offline"}
            </motion.span>
          </div>

          {(() => {
            const isLive = subs?.source === "revenuecat";
            const badge = isLive ? "Live" : "Error";
            const tone = isLive ? "live" : "error";
            const errorHint = subs?.message || "Unable to connect — check API key";
            const trialsVal = isLive ? String(subs.active_trials ?? 0) : "—";
            const totalVal = isLive ? String(subs.total_subscriptions ?? 0) : "—";
            const convVal = isLive
              ? subs.conversion_rate == null
                ? "—"
                : `${subs.conversion_rate}%`
              : "—";
            const liveHint = isLive
              ? "Live from RevenueCat"
              : errorHint;
            return (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <StatCard
                  icon={Users}
                  label="Active Trials"
                  value={trialsVal}
                  hint={liveHint}
                  badge={badge}
                  badgeTone={tone}
                  testid="helm-stat-trials"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Total Subscriptions"
                  value={totalVal}
                  hint={liveHint}
                  badge={badge}
                  badgeTone={tone}
                  testid="helm-stat-subscriptions"
                />
                <StatCard
                  icon={Percent}
                  label="Conversion Rate"
                  value={convVal}
                  hint={isLive ? "Trials that converted to paid" : errorHint}
                  badge={badge}
                  badgeTone={tone}
                  testid="helm-stat-conversion"
                />
              </div>
            );
          })()}
        </motion.section>

        {/* Money In */}
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          data-testid="helm-section-revenue"
        >
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <motion.p variants={fadeUp} className="overline overline-dark mb-3">/ Revenue</motion.p>
              <motion.h3 variants={fadeUp} className="font-display text-3xl text-[#0C2340] md:text-4xl">
                Money In
              </motion.h3>
            </div>
            {subs?.source === "revenuecat" && (subs?.revenue_28d || subs?.mrr) ? (
              <motion.span
                variants={fadeUp}
                className="font-mono-2 rounded-full border border-[#0C2340]/25 bg-[#0C2340]/[0.04] px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] text-[#0C2340]"
              >
                Live — {subs.currency || "USD"}
              </motion.span>
            ) : null}
          </div>

          {(() => {
            const hasRevenue = subs?.source === "revenuecat" && (subs?.revenue_28d || subs?.mrr);
            if (!hasRevenue) {
              return (
                <motion.div variants={fadeUp} className="card-light flex flex-col items-center justify-center gap-3 rounded-sm p-14 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
                    <Wallet size={22} strokeWidth={1.4} />
                  </span>
                  <p className="font-display text-2xl text-[#0C2340]">Revenue tracking activates with first sale.</p>
                  <p className="font-body text-sm text-[#1E3A5F]">A running total will appear here once the first transaction lands.</p>
                </motion.div>
              );
            }
            const fmt = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: subs.currency || "USD",
              maximumFractionDigits: 0,
            });
            return (
              <motion.div variants={fadeUp} className="grid grid-cols-1 gap-6 md:grid-cols-4">
                <div className="card-light rounded-sm p-6" data-testid="helm-revenue-28d">
                  <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
                    <Wallet size={18} strokeWidth={1.5} />
                  </span>
                  <p className="overline overline-dark mt-6">Revenue (28d)</p>
                  <p className="mt-2 font-display text-4xl text-[#0C2340]">{fmt.format(subs.revenue_28d || 0)}</p>
                  <p className="mt-2 font-body text-xs text-[#2E4A68]">Last 28 days</p>
                </div>
                <div className="card-light rounded-sm p-6" data-testid="helm-revenue-mrr">
                  <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
                    <TrendingUp size={18} strokeWidth={1.5} />
                  </span>
                  <p className="overline overline-dark mt-6">MRR</p>
                  <p className="mt-2 font-display text-4xl text-[#0C2340]">{fmt.format(subs.mrr || 0)}</p>
                  <p className="mt-2 font-body text-xs text-[#2E4A68]">Monthly recurring</p>
                </div>
                <div className="card-light rounded-sm p-6" data-testid="helm-revenue-new-customers">
                  <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
                    <Users size={18} strokeWidth={1.5} />
                  </span>
                  <p className="overline overline-dark mt-6">New Customers</p>
                  <p className="mt-2 font-display text-4xl text-[#0C2340]">{subs.new_customers_28d ?? 0}</p>
                  <p className="mt-2 font-body text-xs text-[#2E4A68]">Last 28 days</p>
                </div>
                <div className="card-light rounded-sm p-6" data-testid="helm-revenue-active-users">
                  <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#0C2340]/15 bg-[#0C2340]/5 text-[#0C2340]">
                    <Users size={18} strokeWidth={1.5} />
                  </span>
                  <p className="overline overline-dark mt-6">Active Users</p>
                  <p className="mt-2 font-display text-4xl text-[#0C2340]">{subs.active_users_28d ?? 0}</p>
                  <p className="mt-2 font-body text-xs text-[#2E4A68]">Last 28 days</p>
                </div>
              </motion.div>
            );
          })()}
        </motion.section>
      </div>
    </div>
  );
}

/* ---------------------------------- Page ---------------------------------- */

export default function Helm() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");

  useEffect(() => {
    document.title = "The Helm — Executive Dashboard";
  }, []);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <PasswordGate onAuthenticated={setToken} />;
  return <Dashboard token={token} onSignOut={signOut} />;
}
